import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  type BaseExportFormProps,
  useSocialExportFactory,
} from '@app/modules/social-export/hooks/useSocialExportFactory';
import { socialExportPublishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import { socialExport, webApi } from '@mi-front/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import { ComponentType, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type ComposeSocialExportPublishForm = ComponentType<
  BaseExportFormProps<Record<string, unknown>, socialExport.SettingsGetConnection200>
>;

/** Loaded composition and edits list (fetched by the route page before this hook runs). */
export interface UseComposeSocialExportUploadPageArgs {
  composition: webApi.EnrichedCompositionDetailDto;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
}

export interface UseComposeSocialExportUploadPageResult {
  platform: SocialPlatform;
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: ComposeSocialExportPublishForm;
  handleUpload: (publishData: Record<string, unknown>) => Promise<void>;
}

/**
 * Routing, edit selection, preview URL, and create-upload mutation for composition social export.
 * Expects composition and edits from the page’s detail/edits queries.
 */
export function useComposeSocialExportUploadPage({
  composition,
  edits,
}: UseComposeSocialExportUploadPageArgs): UseComposeSocialExportUploadPageResult {
  const { platform: platformParam } = useParams<{
    compositionId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;
  const editsWithRender = useMemo(
    () => edits.filter((edit) => edit.renderJob?.status === 'COMPLETED'),
    [edits],
  );

  const [selectedSource, setSelectedSource] = useState<string>(() => editIdFromUrl ?? '');

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || selectedSource) return;
    const firstId = editsWithRender[0]?.id;
    if (firstId) setSelectedSource(firstId);
  }, [editIdFromUrl, editsWithRender, selectedSource]);

  const selectedEdit =
    selectedSource !== '' ? (edits.find((edit) => edit.id === selectedSource) ?? null) : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = editsWithRender.length > 0;
  const hasAvailableSources = editsWithRender.length > 0;
  const hasSelectedRenderedEdit = editsWithRender.some((edit) => edit.id === selectedSource);

  const stationId = composition.stationId ?? '';

  const connectionsForStationQuery = webApi.useGetSocialConnectionsConnectionsForStation(
    stationId,
    {
      platform:
        platform.id as webApi.GetSocialConnectionsConnectionsForStationQueryParamsPlatformEnumKey,
    },
    {
      query: { enabled: Boolean(stationId) },
    },
  );

  /**
   * OAuth connection id for this station and platform (required for VIDEO_EDITOR_EDIT uploads).
   * Uses the first connected row returned for the station; results are already scoped by BFF path.
   */
  const connectionId = useMemo(() => {
    const results = connectionsForStationQuery.data?.results ?? [];
    const match = results.find(
      (item) =>
        item.stationId === stationId &&
        item.connected &&
        item.platform.toUpperCase() === platform.id.toUpperCase(),
    );
    return match?.id;
  }, [connectionsForStationQuery.data?.results, stationId, platform.id]);

  const useExport = useSocialExportFactory(platform);

  const uploadMutation = webApi.useCreateSocialUploadCompositionsDetail({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webApi.getCompositionsDetailSocialUploadsQueryKey(composition.id),
        });
        notifyInfo($t(socialExportPublishMessages.publicationStarted));
      },
      onError: () => {
        notifyError($t(socialExportPublishMessages.startPublicationError));
      },
    },
  });

  const handleUpload = async (publishData: Record<string, unknown>) => {
    try {
      if (!hasSelectedRenderedEdit) {
        notifyError($t(socialExportPublishMessages.noRenderedEdit));
        return;
      }

      if (!stationId) {
        notifyError($t(socialExportPublishMessages.noStation));
        return;
      }

      if (connectionsForStationQuery.isLoading) {
        notifyError($t(socialExportPublishMessages.connectionNotReady));
        return;
      }

      if (!connectionId) {
        notifyError(
          $t(socialExportPublishMessages.noOAuthConnectionForPlatform, {
            platformName: platform.name,
          }),
        );
        return;
      }

      await uploadMutation.mutateAsync({
        id: composition.id,
        data: {
          sourceType: webApi.CreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_EDIT,
          sourceId: selectedSource,
          platform: platform.id as webApi.CreateSocialUploadDtoPlatformEnumKey,
          connectionId,
          data: publishData as webApi.CreateSocialUploadDto['data'],
        },
      });
      pushTo(composeRouteGenerator.publish(composition.id));
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const { PublishForm } = useExport();

  return {
    platform,
    selectedSource,
    setSelectedSource,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm: PublishForm as ComposeSocialExportPublishForm,
    handleUpload,
  };
}
